import {useState} from "react";
import {useTranslation} from "react-i18next";
import {css} from "@emotion/react";
import {Checkbox} from "@pg-design/checkbox";
import {
    backgroundColor,
    flex,
    flexAlignCenter,
    flexDirection,
    flexJustifyCenter,
    h100,
    mb,
    mt,
    onDesktop,
    p,
    pb,
    ph,
    pointer,
    pt,
    pv,
    textAlign,
    underline,
    w100
} from "@pg-design/helpers-css";
import {CloseIcon} from "@pg-design/icons";
import {Text} from "@pg-design/text";

import {useAppDispatch, useAppSelector} from "../../../../../../../utils/hooks/store_hooks";
import {ZIndex} from "../../../../../common/app/constants/z_index";
import {flexGap} from "../../../../../components/css_helpers/flex";
import {ResponsiveModal} from "../../../../../components/responsive_modal/ResponsiveModal";
import {selectAuthStatus, setAuthModal} from "../../../../auth/auth_slice";
import {useCreateCheckoutSessionMutation} from "../../../api/subscriptions_api";
import {
    buildCheckoutRedirectUrls,
    FREE_PLAN_FEATURE_KEYS,
    getStripePriceId,
    PAID_PLAN_FEATURE_KEYS,
    SubscriptionPeriod
} from "./subscription_modal.constants";
import {SubscriptionPeriodPills} from "./SubscriptionPeriodPills";
import {SubscriptionPlanCard} from "./SubscriptionPlanCard";

import logoBdLite from "../../../../../public/rynek-pierwotny-pl-big-data-logo-lite.svg";

interface IBigDataInsightSubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    returnPath: string;
}

const noop = () => undefined;

export const BigDataInsightSubscriptionModal = ({
    isOpen,
    onClose,
    returnPath
}: IBigDataInsightSubscriptionModalProps) => {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const {isLoggedIn} = useAppSelector(selectAuthStatus);
    const [selectedPeriod, setSelectedPeriod] = useState<SubscriptionPeriod>("monthly");
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [checkoutError, setCheckoutError] = useState<string | null>(null);
    const [createCheckoutSession, {isLoading: isCheckoutLoading}] = useCreateCheckoutSessionMutation();

    const freeFeatures = FREE_PLAN_FEATURE_KEYS.map((key) => t(key));
    const paidFeatures = PAID_PLAN_FEATURE_KEYS.map((key) => t(key));

    const handleLogin = () => {
        dispatch(setAuthModal("login"));
    };

    const handlePaidCheckout = async () => {
        setCheckoutError(null);

        if (!isLoggedIn) {
            dispatch(setAuthModal("login"));
            return;
        }

        if (!termsAccepted) {
            setCheckoutError(t("newshub.paywall.modal.checkout.terms_required"));
            return;
        }

        const returnUrl = `${window.location.origin}${returnPath}`;
        const {success_url, cancel_url} = buildCheckoutRedirectUrls(returnUrl);

        try {
            const {url} = await createCheckoutSession({
                price_id: getStripePriceId(selectedPeriod),
                success_url,
                cancel_url
            }).unwrap();

            window.location.assign(url);
        } catch {
            setCheckoutError(t("newshub.paywall.modal.checkout.error"));
        }
    };

    const paidPrice =
        selectedPeriod === "monthly" ? (
            <div css={priceRow}>
                <Text variant="headline_2" as="span">
                    PLN {t("newshub.paywall.modal.plans.paid.monthly_price")}
                </Text>
                <Text variant="body_copy_1" as="span" css={pricePeriod}>
                    {t("newshub.paywall.modal.plans.paid.monthly_period")}
                </Text>
            </div>
        ) : (
            <div css={priceRow}>
                <Text variant="headline_2" as="span">
                    PLN {t("newshub.paywall.modal.plans.paid.yearly_price")}
                </Text>
                <Text variant="body_copy_1" as="span" css={pricePeriod}>
                    {t("newshub.paywall.modal.plans.paid.yearly_period")}
                </Text>
            </div>
        );

    return (
        <ResponsiveModal isOpen={isOpen} fullScreen maxWidth="100%" onModalClose={onClose} zIndex={ZIndex.MODAL}>
            <div css={modalContent}>
                <div css={header}>
                    <button type="button" css={closeButton} onClick={onClose} aria-label="Close">
                        <CloseIcon size="2" />
                    </button>
                </div>

                <div css={pageWrapper}>
                    <div css={logoRow}>
                        <div css={logoIcon}>
                            <img src={logoBdLite} alt="" css={logoIconImage} loading="lazy" />
                        </div>
                        <Text variant="headline_1" css={logoTitle}>
                            {t("newshub.paywall.modal.title")}
                        </Text>
                    </div>

                    <div css={formSection}>
                        {isLoggedIn && (
                            <div css={termsWrapper}>
                                <Checkbox
                                    name="subscription_terms_accepted"
                                    id="subscription_terms_accepted"
                                    checked={termsAccepted}
                                    onChange={(_name, checked) => setTermsAccepted(checked)}
                                    labelContent={
                                        <Text variant="info_txt_1" as="span" css={textAlign("center")}>
                                            {t("newshub.paywall.modal.form.terms_prefix")}{" "}
                                            <a href="#" css={termsLink}>
                                                {t("newshub.paywall.modal.form.terms_link")}
                                            </a>{" "}
                                            {t("newshub.paywall.modal.form.terms_and")}{" "}
                                            <a href="#" css={termsLink}>
                                                {t("newshub.paywall.modal.form.privacy_link")}
                                            </a>
                                        </Text>
                                    }
                                />
                            </div>
                        )}

                        <div css={periodToggle}>
                            <SubscriptionPeriodPills
                                selectedPeriod={selectedPeriod}
                                onChange={setSelectedPeriod}
                                monthlyLabel={t("newshub.paywall.modal.period.monthly")}
                                yearlyLabel={t("newshub.paywall.modal.period.yearly")}
                            />
                        </div>
                    </div>

                    <div css={plansRow}>
                        <SubscriptionPlanCard
                            title={t("newshub.paywall.modal.plans.free.title")}
                            price={
                                <Text variant="headline_2" as="span">
                                    PLN 0
                                </Text>
                            }
                            subtitle={t("newshub.paywall.modal.plans.free.subtitle")}
                            features={freeFeatures}
                            ctaLabel={t("newshub.paywall.modal.plans.free.cta")}
                            onCtaClick={noop}
                        />
                        <SubscriptionPlanCard
                            title={t("newshub.paywall.modal.plans.paid.title")}
                            price={paidPrice}
                            badge={t("newshub.paywall.modal.plans.paid.trial_badge")}
                            discountLabel={
                                selectedPeriod === "annually"
                                    ? t("newshub.paywall.modal.plans.paid.yearly_discount")
                                    : undefined
                            }
                            description={t("newshub.paywall.modal.plans.paid.description")}
                            features={paidFeatures}
                            ctaLabel={t("newshub.paywall.modal.plans.paid.cta")}
                            onCtaClick={handlePaidCheckout}
                            ctaDisabled={isCheckoutLoading}
                        />
                    </div>

                    {checkoutError && (
                        <Text variant="info_txt_1" css={[textAlign("center"), checkoutErrorText]}>
                            {checkoutError}
                        </Text>
                    )}

                    {isLoggedIn && (
                        <Text variant="info_txt_1" css={[textAlign("center"), disclaimer, ph(2)]}>
                            {t("newshub.paywall.modal.disclaimer")}
                        </Text>
                    )}

                    {!isLoggedIn && (
                        <Text variant="body_copy_1" as="div" css={[textAlign("center"), mb(3)]}>
                            {t("newshub.paywall.login_prompt")}{" "}
                            <span role="button" tabIndex={0} css={loginLink} onClick={handleLogin}>
                                {t("newshub.paywall.login_link")}
                            </span>
                        </Text>
                    )}
                </div>
            </div>
        </ResponsiveModal>
    );
};

const modalContent = css`
    ${flex()};
    ${flexDirection("column")};
    ${flexAlignCenter};
    ${w100};
    height: 100%;
    max-height: 100vh;
    overflow-y: auto;
    ${backgroundColor("#fff")};
`;

const header = css`
    position: sticky;
    top: 0;
    z-index: 1;
    align-self: stretch;
    ${flex("center", "flex-end")};
    ${p(2, 3, 0, 3)};
    ${backgroundColor("#fff")};
`;

const pageWrapper = css`
    ${flex()};
    ${flexDirection("column")};
    width: 100%;
    max-width: 110rem;
    ${ph(3)};
    ${pb(3)};
`;

const closeButton = css`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    ${flex()};
    ${flexAlignCenter};
`;

const logoRow = css`
    ${flex("center", "center")};
    ${flexDirection("column")};
    ${flexGap(1)};
    ${pv(2)};
    flex-shrink: 0;
`;

const logoIcon = css`
    width: 6.4rem;
    height: 6.4rem;
    overflow: hidden;
    flex-shrink: 0;
`;

const logoIconImage = css`
    display: block;
    height: 6.4rem;
    width: auto;
    max-width: none;
`;

const logoTitle = css`
    ${textAlign("center")};
`;

const formSection = css`
    ${mb(3)};
    max-width: 56rem;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
`;

const termsWrapper = css`
    ${flexJustifyCenter};
    ${mt(2)};
`;

const periodToggle = css`
    ${flexJustifyCenter};
    ${mt(3)};
    ${mb(1)};
`;

const plansRow = css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.6rem;
    ${mb(3)};
    ${mt(2)};
    align-items: stretch;
    width: 100%;

    ${onDesktop(css`
        grid-template-columns: 1fr 1fr;
    `)};

    & > * {
        ${h100};
        min-width: 0;
    }
`;

const priceRow = css`
    ${flex("baseline", "flex-start")};
    ${flexGap(0.5)};
    flex-wrap: wrap;
`;

const pricePeriod = css`
    color: #757575;
`;

const checkoutErrorText = css`
    color: #c62828;
    ${pt(2)};
    ${mb(2)};
`;

const disclaimer = css`
    color: #757575;
    ${pt(4)};
    ${mb(2)};
`;

const termsLink = css`
    ${underline};
    color: inherit;
`;

const loginLink = css`
    ${underline};
    ${pointer};
    font-weight: 600;
`;
